import { ok, type Result } from '@src/services/common/Result';
import { extractErrorInfo } from '@src/utility/error';
import { LogManager } from '@src/utility/Logger';
import {
  type ClipTheme,
  getTopicParagraphMapping,
  TOPIC_PARAGRAPH_MAPPING_VERSION,
  type TopicParagraphMapping,
} from '@src/utility/story';
import {
  computeTopicsHash,
  computeTranscriptHash,
} from '@src/utility/topicTaggingHash';
import {
  groupElementsByParagraph,
  hasMeaningfulParagraphs,
} from '@src/utility/transcriptGrouping';

import type { TopicTaggingService } from './TopicTaggingService';
import type { Story } from '@src/types.ts/story';
import type { TranscriptElement } from '@src/types.ts/transcript';

const logger = LogManager.getLogger('service:TopicMappingOrchestrator');

/**
 * Narrow persistence interface for topic-paragraph mappings. The DatoCMS
 * `StoryRepository` already satisfies this structurally; co-locating the
 * interface here keeps the orchestrator decoupled from the full repository
 * surface (Phase 2: narrow interfaces co-located alongside each service).
 */
export interface IStoryTopicWriter {
  updateTopicParagraphMapping(
    storyId: string,
    mapping: TopicParagraphMapping,
  ): Promise<void>;
}

export interface GetOrComputeMappingInput {
  story: Story;
  storyId: string;
  topics: ClipTheme[];
  originalElements: TranscriptElement[];
  userIdentity?: { email?: string; id?: string };
  signal?: AbortSignal;
  /**
   * Side-effect hook invoked after a fresh mapping is produced (cache miss
   * path only). Lets the caller mirror the newly-computed mapping into an
   * in-memory store so subsequent mounts within the same session cache-hit
   * without re-calling the AI. Not called on cache hit (the data is already
   * in `story.aiResponse`).
   *
   * The orchestrator stays MobX-free — wrapping in `runInAction` or routing
   * through a store action is the caller's responsibility.
   */
  applyMapping?: (mapping: TopicParagraphMapping) => void;
}

/**
 * Orchestrates topic-paragraph tagging:
 *   1. structural-hash cache lookup against `story.aiResponse`
 *   2. build paragraph payload via `groupElementsByParagraph`
 *   3. AI call via injected `TopicTaggingService`
 *   4. persistence via injected `IStoryTopicWriter`
 *
 * Pure orchestration — no MobX, no React, no observable mutation. The caller
 * (a React hook) is responsible for surfacing the returned mapping into UI
 * state and for AbortController lifecycle.
 *
 * Result contract:
 *   - `ok(mapping)`  — fresh or cached topic-paragraph mapping is available.
 *   - `ok(null)`     — no work to do (empty topics/elements, non-meaningful
 *                      paragraphs, or no extractable paragraph text). The
 *                      caller should treat this as an idle/skip state, not as
 *                      an error.
 *   - `err(...)`     — AI call failed; persistence failures are swallowed
 *                      (logged) and downgraded to `ok(mapping)`.
 */
export class TopicMappingOrchestrator {
  constructor(
    private readonly tagging: TopicTaggingService,
    private readonly storyWriter: IStoryTopicWriter,
  ) {}

  async getOrCompute(
    input: GetOrComputeMappingInput,
  ): Promise<Result<TopicParagraphMapping | null>> {
    const {
      story,
      storyId,
      topics,
      originalElements,
      userIdentity,
      signal,
      applyMapping,
    } = input;

    if (!topics.length || !originalElements.length) {
      return ok(null);
    }

    if (!hasMeaningfulParagraphs(originalElements)) {
      logger.warn(
        'Transcript lacks meaningful paragraphs; skipping topic tagging',
        { storyId },
      );
      return ok(null);
    }

    const topicsHash = computeTopicsHash(topics.map(t => t.title));
    const transcriptHash = computeTranscriptHash(originalElements);

    const cached = getTopicParagraphMapping(story.aiResponse);
    if (
      cached &&
      cached.topicsHash === topicsHash &&
      cached.transcriptHash === transcriptHash
    ) {
      return ok(cached);
    }

    const paragraphs = buildParagraphPayload(originalElements);
    if (paragraphs.length === 0) {
      return ok(null);
    }

    const taggingResult = await this.tagging.tagParagraphs({
      paragraphs,
      topics,
      storyId,
      storyName: story.title,
      userEmail: userIdentity?.email,
      userId: userIdentity?.id,
      signal,
    });
    if (!taggingResult.ok) return taggingResult;

    const mapping: TopicParagraphMapping = {
      version: TOPIC_PARAGRAPH_MAPPING_VERSION,
      generatedAt: Date.now(),
      topicsHash,
      transcriptHash,
      byTopic: taggingResult.value.byTopic,
    };

    try {
      await this.storyWriter.updateTopicParagraphMapping(storyId, mapping);
    } catch (e) {
      logger.error(
        'Failed to persist topicParagraphMapping; returning in-memory result',
        extractErrorInfo(e),
      );
      // Persistence failed but we still have a valid mapping in-memory;
      // surface it to the UI so the user can filter this session.
    }

    // Mirror the fresh mapping into the caller's in-memory store so the next
    // mount within this session hits the cache instead of re-calling the AI.
    // Fires regardless of persist outcome — local state should reflect what
    // the UI is about to show.
    applyMapping?.(mapping);

    return ok(mapping);
  }
}

function buildParagraphPayload(
  originalElements: TranscriptElement[],
): { paragraphId: string; speakerLabel?: string; text: string }[] {
  const groups = groupElementsByParagraph(originalElements);
  return groups
    .map(group => {
      if (!group.paragraphId) return null;
      const text = group.elements
        .map(e => (typeof e.element.value === 'string' ? e.element.value : ''))
        .join('')
        .replace(/\s+/g, ' ')
        .trim();
      if (!text) return null;
      return {
        paragraphId: group.paragraphId,
        speakerLabel: group.speakerLabel,
        text,
      };
    })
    .filter((p): p is NonNullable<typeof p> => p !== null);
}
